// SendIt.jsx
import React from 'react';
import html2pdf from 'html2pdf.js';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import * as XLSX from 'xlsx';
import {
    sendItFolderMessage,
    sendItFolderPDF,
    sendItFolderDocx,
    sendItFolderXlsx
} from './SendItFolder';
import {
    sendItEmailMessage,
    sendItEmailXlsx,
    sendItEmailDocx,
    sendItEmailPDF
} from './SendItEmail';

// New OneWriter functions
const sendItEmailOneFile = () => {
    console.log("Ready: sendItEmailOneFile");

    // Get filename from DOM (same pattern as other email functions)
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Export';

    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    // Click the save button to download .onewriter file
    const saveButton = document.getElementById('buttonSaveFile');
    if (saveButton) {
        saveButton.click();
        console.log('Clicked buttonSaveFile to download .onewriter file');
    } else {
        console.log('Save button not found');
        alert('Save button not found - cannot download OneWriter file.');
        return;
    }

    // Open email client with instructions (same pattern as PDF/DOCX/XLSX)
    const subject = encodeURIComponent(`OneWriter Export: ${fileName}`);
    const body = encodeURIComponent(
        `The OneWriter file "${fileName}.onewriter" has been downloaded to your device. ` +
        `Please attach it to this email before sending.\n\n`
    );
    const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${subject}&body=${body}`;

    // Small delay to ensure download starts first
    setTimeout(() => {
        window.open(gmailURL, '_blank');
    }, 1000);
};

const sendItFolderOneFile = () => {
    console.log("Ready: sendItFolderOneFile");

    // Click the save button to download .onewriter file
    const saveButton = document.getElementById('buttonSaveFile');
    if (saveButton) {
        saveButton.click();
        console.log('Clicked buttonSaveFile to download .onewriter file');
    } else {
        console.log('Save button not found');
        alert('Save button not found - cannot download OneWriter file.');
    }
};

const sendItPrinter = (orientationMode = 'portrait') => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');

    if (previewElements.length === 0) {
        alert('No content found to print.');
        return;
    }

    // Get filename
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Print';
    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    console.log(`Creating PDF for printing: ${fileName}.pdf`);

    const container = document.createElement('div');
    container.style.background = 'white';
    container.style.width = orientationMode === 'landscape' ? '11in' : '8.5in';

    previewElements.forEach((preview, index) => {
        const clone = preview.cloneNode(true);

        // Remove the document frame
        clone.style.border = 'none';

        const contentElements = clone.querySelectorAll('.print-preview-content');
        contentElements.forEach(contentEl => {
            contentEl.style.maxWidth = 'none';
            contentEl.style.width = 'auto';
        });

        clone.style.width = '100%';
        clone.style.minHeight = orientationMode === 'landscape' ? '7.5in' : '10in';
        clone.style.margin = '0';
        clone.style.padding = '0.5in';
        clone.style.boxSizing = 'border-box';
        clone.style.display = 'block';
        clone.style.fontSize = '16px';
        clone.style.lineHeight = '1.5';

        if (index < previewElements.length - 1) {
            clone.style.pageBreakAfter = 'always';
        }

        container.appendChild(clone);
    });

    document.body.appendChild(container);

    const opt = {
        margin: 0,
        filename: `${fileName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            letterRendering: true
        },
        jsPDF: {
            unit: 'in',
            format: 'letter',
            orientation: orientationMode
        }
    };

    html2pdf()
        .set(opt)
        .from(container)
        .outputPdf('blob')
        .then((pdfBlob) => {
            // Create a URL for the PDF blob
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Open PDF in new tab
            const printWindow = window.open(pdfUrl, '_blank');

            // Clean up the container
            container.remove();

            // Wait for the PDF to load in the new tab, then trigger print
            if (printWindow) {
                printWindow.onload = () => {
                    // Small delay to ensure PDF is fully loaded
                    setTimeout(() => {
                        printWindow.print();
                    }, 1000);
                };

                // Fallback: if onload doesn't work, try after a longer delay
                setTimeout(() => {
                    try {
                        printWindow.print();
                    } catch (e) {
                        console.log('Print dialog fallback - user can manually print from browser');
                    }
                }, 2000);
            } else {
                alert('Pop-up blocked. Please allow pop-ups and try again.');
            }

            // Clean up the blob URL after some time
            setTimeout(() => {
                URL.revokeObjectURL(pdfUrl);
            }, 10000);
        })
        .catch((err) => {
            console.error('PDF generation for printing failed:', err);
            container.remove();
            alert('Failed to generate PDF for printing.');
        });
};

const sendItScanAndSend = () => {
    // --- FIRST SLIDER ---
    const sendToggle = document.querySelector('.send-toggle-switch');
    const sendModes = ['In an Email', 'To a Folder', 'To an App', 'To a Printer'];
    const sendIndex = getCheckedIndex(sendToggle);
    const sendMode = sendModes[sendIndex] || 'unknown';
    console.log(`selection 1: "${sendMode}"`);

    // --- THIRD SLIDER ---
    const tertiaryToggle = document.querySelector('.send-tertiary-toggle-switch');
    const tertiaryOptions = ['Message', 'Pdf', 'Docx', 'Xlsx', 'Onewriter'];
    const tertiaryIndex = getCheckedIndex(tertiaryToggle);
    const tertiaryMode = tertiaryOptions[tertiaryIndex] || 'unknown';
    console.log(`selection 3: "${tertiaryMode}"`);

    // --- ORIENTATION SLIDER ---
    const orientationToggle = document.querySelector('.send-orientation-toggle-switch');
    const orientationIndex = getCheckedIndex(orientationToggle);
    const orientationMode = orientationIndex === 0 ? 'portrait' : 'landscape';
    console.log(`orientation: "${orientationMode}"`);

    // === ONEWRITER CASES ===
    if (tertiaryMode === 'Onewriter') {
        if (sendMode === 'In an Email') {
            sendItEmailOneFile();
        } else if (sendMode === 'To a Folder') {
            sendItFolderOneFile();
        } else if (sendMode === 'To an App') {
            // Default to existing "coming soon" notification
            const notification = document.createElement('div');
            notification.style.cssText = `
                position: fixed;
                top: 20px;
                left: 50%;
                transform: translateX(-50%);
                background: #333;
                color: white;
                padding: 15px 25px;
                border-radius: 8px;
                z-index: 10000;
                font-family: Arial, sans-serif;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            `;
            notification.textContent = '"Send to an App" feature is coming soon…';
            document.body.appendChild(notification);

            // Remove notification after 3 seconds
            setTimeout(() => {
                notification.remove();
            }, 3000);
        } else if (sendMode === 'To a Printer') {
            // Default to existing printer behavior
            sendItPrinter(orientationMode);
        }
    }
    // === EMAIL CASES (non-OneWriter) ===
    else if (sendMode === 'In an Email' && tertiaryMode === 'Message') {
        sendItEmailMessage();
    } else if (sendMode === 'In an Email' && tertiaryMode === 'Pdf') {
        sendItEmailPDF(orientationMode);
    } else if (sendMode === 'In an Email' && tertiaryMode === 'Docx') {
        sendItEmailDocx();
    } else if (sendMode === 'In an Email' && tertiaryMode === 'Xlsx') {
        sendItEmailXlsx();
    }
    // === FOLDER CASES (non-OneWriter) ===
    else if (sendMode === 'To a Folder' && tertiaryMode === 'Message') {
        sendItFolderMessage();
    } else if (sendMode === 'To a Folder' && tertiaryMode === 'Pdf') {
        sendItFolderPDF(orientationMode);
    } else if (sendMode === 'To a Folder' && tertiaryMode === 'Docx') {
        sendItFolderDocx();
    } else if (sendMode === 'To a Folder' && tertiaryMode === 'Xlsx') {
        sendItFolderXlsx();
    }
    // === PRINTER CASE (non-OneWriter) ===
    else if (sendMode === 'To a Printer') {
        sendItPrinter(orientationMode);
    }
    // === APP CASE (non-OneWriter) - Show coming soon notification ===
    else if (sendMode === 'To an App') {
        // Create a custom notification to avoid browser's "localhost:3000 says" prefix
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: #333;
            color: white;
            padding: 15px 25px;
            border-radius: 8px;
            z-index: 10000;
            font-family: Arial, sans-serif;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        `;
        notification.textContent = '"Send to an App" feature is coming soon…';
        document.body.appendChild(notification);

        // Remove notification after 3 seconds
        setTimeout(() => {
            notification.remove();
        }, 3000);
    }
};

const getCheckedIndex = (container) => {
    if (!container) return -1;
    const checkedInput = container.querySelector('input[type="radio"]:checked');
    return Array.from(container.querySelectorAll('input[type="radio"]')).indexOf(checkedInput);
};

const SendIt = () => {
    return (
        <button
            id="buttonSendIt"
            className="responsive-icon-button appHeaderBarColRight-BoxLogoMenu5of5Button"
            onClick={sendItScanAndSend}
        >
            SEND PROJECT
        </button>
    );
};

export default SendIt;
